// HeroSection.jsx
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export default function HeroSection() {
  const navigate = useNavigate();
  const heroRef = useRef(null);

useEffect(() => {
  const section = heroRef.current;
  if (!section) return;

  const items = section.querySelectorAll(".hero-left-item");
  const visuals = section.querySelector(".hero-right-visuals");

  // Delay hero animation until intro logo finishes
  const startDelay = 2500; // ms

  const startAnimations = () => {
    items.forEach((item, i) =>
      setTimeout(() => item.classList.add("visible"), i * 200)
    );

    setTimeout(() => visuals.classList.add("visible"), 600);
  };

  const timer = setTimeout(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startAnimations();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(section);
  }, startDelay);

  return () => clearTimeout(timer);
}, []);

  return (
    <>
      <style>{`
        .hero-left-item {
          opacity: 0;
          transform: translateY(25px);
          transition: all .7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hero-left-item.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .hero-right-visuals {
          opacity: 0;
          transform: translateY(30px) scale(0.96);
          transition: all 1s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hero-right-visuals.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      `}</style>

      <section ref={heroRef} className="py-14">
        <div className="flex flex-col lg:flex-row items-center gap-12">

          {/* LEFT CONTENT */}
          <div className="flex-1 text-center lg:text-left px-2">
            <h1 className="hero-left-item text-3xl sm:text-4xl lg:text-5xl leading-tight text-gray-600">
              Smart Digital Solutions
            </h1>

            <span className="hero-left-item shine block font-copperplate text-[#785f14] text-2xl sm:text-3xl lg:text-3xl mt-4">
              Built for Every Business
            </span>

            <p className="hero-left-item mt-4 text-gray-600 text-sm sm:text-base max-w-lg mx-auto lg:mx-0">
              Custom software, Websites, Shopify stores, mobile apps and  dashboards —
              everything your business needs to go digital — affordably and professionally.
            </p>

            <div className="hero-left-item mt-6 flex flex-col shine sm:flex-row gap-3 justify-center lg:justify-start">
              <button
                onClick={() => navigate('/services')}
                className="
                  px-7 py-3 rounded-lg w-full sm:w-auto
                  bg-gradient-to-r from-brand to-brand-hover
                  text-white font-medium text-sm shadow-md
                  hover:shadow-lg hover:scale-[1.03] active:scale-95 transition
                "
              >
                View Services
              </button>

              <button
                onClick={() => navigate('/contact#book')}
                className="
                  px-7 py-3 rounded-lg w-full sm:w-auto
                  border border-[#785f14]
                  text-[#785f14] font-medium text-sm
                  hover:bg-[#785f14]/10 transition 
                  hover:scale-[1.03] active:scale-95
                "
              >
                Book a Free Call
              </button>
            </div>
          </div>

          {/* RIGHT VISUALS — UNCHANGED, ONLY WRAPPED FOR ANIMATION */}
          <div className="hero-right-visuals flex-1 flex justify-center relative">

            {/* Soft glow */}
            <div className="
              absolute inset-0 blur-3xl rounded-full
              bg-[#785f14]/15 -z-10 w-72 h-72 mx-auto
            "></div>

            {/* EXACT DEVICE MOCKUPS YOU ALREADY HAVE */}
            <div className="relative w-64 sm:w-72 md:w-80">

              {/* Floating mini icons */}
              <div className="absolute -top-4 -left-6 flex flex-col gap-3 animate-float-slow-3">
                <div className="p-2 bg-white shadow-md rounded-lg border border-[#785f14]/10">
                  <svg className="w-6 h-6 text-[#785f14]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M3 7h18M5 7l1 12h12l1-12M8 7V5a4 4 0 118 0v2" />
                  </svg>
                </div>

                <div className="p-2 bg-white shadow-md rounded-lg border border-[#785f14]/10 animate-float-slow-2">
                  <svg className="w-6 h-6 text-[#785f14]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M10.5 6h3m-5.25 13.5L9 15m6 4.5L14.25 15M4.5 9h15M4.5 12h15M4.5 15h15" />
                  </svg>
                </div>
              </div>

              {/* LAPTOP MOCKUP */}
              <div className="rounded-xl border border-gray-200 shadow-lg bg-white overflow-hidden">
                <div className="relative h-40 sm:h-48 bg-gray-100 overflow-hidden shine">

                  {/* Big website card */}
                  <div className="
                    absolute left-5 top-5 
                    w-32 sm:w-40 h-20 
                    bg-white rounded-xl shadow-md border border-gray-200
                    animate-float-slow
                  ">
                    <div className="h-6 bg-[#785f14]/10 rounded-t-xl flex items-center px-2 gap-1">
                      <span className="w-2 h-2 rounded-full bg-[#785f14]/60" />
                      <span className="w-8 h-2 rounded bg-gray-300" />
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="w-16 h-2 rounded bg-gray-300" />
                      <div className="w-20 h-2 rounded bg-gray-200" />
                      <div className="w-14 h-2 rounded bg-gray-200" />
                    </div>
                  </div>

                  {/* Dashboard stats card */}
                  <div className="
                    absolute right-4 top-10 
                    w-24 sm:w-28 h-16 
                    bg-white rounded-lg shadow-md border border-gray-200
                    animate-float-slow-2
                  ">
                    <div className="p-2 space-y-1">
                      <div className="w-10 h-2 rounded bg-gray-300" />
                      <div className="flex items-end gap-1 mt-1">
                        <div className="w-2 h-4 bg-[#785f14]/50 rounded" />
                        <div className="w-2 h-6 bg-[#785f14]/70 rounded" />
                        <div className="w-2 h-3 bg-[#785f14]/40 rounded" />
                        <div className="w-2 h-5 bg-[#785f14]/60 rounded" />
                      </div>
                    </div>
                  </div>

                  {/* Mobile UI card */}
                  <div className="
                    absolute left-1/2 -bottom-2 -translate-x-1/2
                    w-16 sm:w-20 h-28
                    bg-white rounded-2xl shadow-md border border-gray-200
                    animate-float-slow-3
                  ">
                    <div className="h-3 bg-[#785f14]/15 rounded-t-2xl" />
                    <div className="p-2 space-y-1">
                      <div className="w-10 h-2 rounded bg-gray-300" />
                      <div className="w-12 h-2 rounded bg-gray-200" />
                      <div className="w-12 h-8 rounded bg-gray-100 mt-1" />
                      <div className="w-10 h-2 rounded bg-[#785f14]/30 mt-1" />
                    </div>
                  </div>

                </div>

                <div className="h-3 bg-gray-200" />
              </div>

              {/* PHONE MOCKUP */}
              <div className="
                absolute -right-8 -bottom-8 
                w-24 sm:w-28 rounded-xl shadow-lg bg-white border border-gray-200
                overflow-hidden animate-float-slow
              ">
                <div className="h-32 bg-gray-100 flex flex-col justify-center items-center gap-2">
                  <div className="w-14 h-2 rounded bg-gray-300" />
                  <div className="w-16 h-2 rounded bg-gray-200" />
                  <div className="w-12 h-2 rounded bg-gray-200" />
                  <div className="w-10 h-2 rounded bg-[#785f14]/40" />
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>
    </>
  );
}
